import { useModeAwareQuery } from "./useModeAwareQuery";
import { useApi } from "./useApi";
import type { CollectionCase, CaseNote, CaseAuditLog } from "@/types/collections";
import type { CollectionCaseListItemApi } from "./useCollectionCasesData";
import type { Transaction } from "@/types/transaction";
import type { ContactUI } from "@/types/ui/customer-detail";
import { getGlobalDataStore } from "@/lib/mock-data/global-data-store";


interface CaseActivityApi {
  activityId: string;
  caseId: string;
  activityType: string;
  description: string;
  outcome: string | null;
  createdBy: string;
  createdAt: string;
  metadataJson: string | null;
}

interface CaseNoteApi {
  id: number;
  caseId: string;
  noteType: string;
  content: string;
  isPrivate: boolean;
  isPinned: boolean;
  createdByUserId: number;
  createdByUserName: string;
  createdAt: string;
  updatedByUserId: number | null;
  updatedByUserName: string | null;
  updatedAt: string | null;
}

interface CaseCommunicationApi {
  id: number;
  caseId: string;
  communicationType: string;
  direction: string;
  subject: string | null;
  content: string;
  status: string;
  sentAt: string | null;
  sentByUserId: number | null;
  sentByUserName: string | null;
  recipientEmail: string | null;
  recipientPhone: string | null;
  metadata: string | null;
  createdAt: string;
}

interface CaseAuditLogApi {
  id: number;
  caseId: number;
  caseNumber: string;
  actionTypeId: number;
  actionTypeName: string;
  description: string;
  fieldName: string | null;
  oldValue: string | null;
  newValue: string | null;
  transactionId: number | null;
  transactionNumber: string | null;
  transactionAmount: number | null;
  transactionDate: string | null;
  metadata: string | null;
  createdByUserId: number;
  createdByUserName: string;
  createdAt: string;
}

interface ContactsApiResponse {
  contacts: {
    id: number;
    title: string | null;
    firstName: string;
    surname: string;
    email: string;
    phone: string;
    mobile: string | null;
    preferredContactMethod: string | null;
    jobTitle: string | null;
    isPrimary: boolean;
    company: string | null;
    address: string | null;
    notes: string | null;
    createdAt: string | null;
    lastContact: string | null;
  }[];
}

function computeDaysInCollection(createdAt: string): number {
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 0;
  const createdUTC = Date.UTC(created.getUTCFullYear(), created.getUTCMonth(), created.getUTCDate());
  const now = new Date();
  const nowUTC = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  return Math.max(0, Math.round((nowUTC - createdUTC) / 86_400_000));
}

function mapApiCaseToCollectionCase(api: CollectionCaseListItemApi): CollectionCase {
  const lastAction = api.lastActionType
    ? {
        type: api.lastActionType,
        description: api.lastActionDescription || "",
        date: api.lastActionDate ? new Date(api.lastActionDate) : new Date(),
        performedBy: api.lastActionActorName || "Unknown",
      }
    : undefined;

  return {
    id: api.caseId,
    caseId: api.caseNumber || api.caseId,
    customerId: String(api.customerId),
    customerName: api.customerName,
    customerEmail: api.customerEmail || "",
    customerPhone: api.customerPhone || "",
    priority: (api.priority as CollectionCase["priority"]) || "Medium",
    status: (api.status as CollectionCase["status"]) || "Active",
    strategy: {
      id: api.strategyId ? String(api.strategyId) : "",
      name: api.strategyId ? (api.strategyName || "Default") : "Manual",
      type: "Standard",
    },
    strategyPhase: api.strategyId
      ? {
          currentPhase: api.currentStage,
          totalPhases: api.totalStages,
          phaseName: `Stage ${api.currentStage}`,
        }
      : {
          currentPhase: 1,
          totalPhases: 1,
          phaseName: "Manual",
        },
    amount: api.amount,
    daysInCollection: api.daysInCollection || computeDaysInCollection(api.createdAt),
    nextAction: api.nextAction || "No action scheduled",
    nextActionDate: api.nextActionDate ? new Date(api.nextActionDate) : new Date(),
    riskLevel: (api.riskLevel as CollectionCase["riskLevel"]) || undefined,
    createdAt: new Date(api.createdAt),
    updatedAt: api.updatedAt ? new Date(api.updatedAt) : new Date(),
    isPaused: api.isPaused,
    assignedTo: api.assignedToName || undefined,
    contactId: api.contactId ? String(api.contactId) : undefined,
    assignedUserId: api.assignedTo || undefined,
    notes: api.notes || undefined,
    currency: api.currencyCode || undefined,
    lastAction,
  };
}

const actionTypeToActionMap: Record<string, CaseAuditLog["action"]> = {
  status_change: "status_change",
  transaction_added: "transaction_added",
  transaction_removed: "transaction_removed",
  priority_change: "priority_change",
  assignment_change: "assignment_change",
  field_update: "field_update",
  note_added: "note_added",
  case_created: "case_created",
};

function mapApiAuditLogToCaseAuditLog(api: CaseAuditLogApi): CaseAuditLog {
  const actionName = api.actionTypeName.toLowerCase().replace(/\s+/g, "_");
  return {
    id: String(api.id),
    caseId: String(api.caseId),
    action: actionTypeToActionMap[actionName] || "field_update",
    description: api.description,
    fieldName: api.fieldName || undefined,
    oldValue: api.oldValue,
    newValue: api.newValue,
    transactionId: api.transactionId ? String(api.transactionId) : undefined,
    transactionNumber: api.transactionNumber || undefined,
    transactionAmount: api.transactionAmount || undefined,
    transactionDate: api.transactionDate ? new Date(api.transactionDate) : undefined,
    createdBy: api.createdByUserName,
    createdAt: new Date(api.createdAt),
  };
}

interface CaseActivity {
  id: string;
  caseId: string;
  type: "call" | "email" | "note" | "sms" | "promise" | "payment" | "status_change";
  description: string;
  outcome?: string;
  createdBy: string;
  createdAt: Date;
}

interface CaseCommunication {
  id: string;
  caseId: string;
  type: "email" | "phone" | "sms" | "letter";
  direction: "inbound" | "outbound";
  subject?: string;
  content: string;
  sentAt: Date;
  sentBy: string;
  status: "sent" | "delivered" | "failed" | "bounced";
}

export function useCollectionCasesList() {
  const { apiGet } = useApi();

  const mockCases = getGlobalDataStore().getAllCases();

  return useModeAwareQuery<CollectionCase[], Error>(
    ["collection-cases-list"],
    async ({ signal }) => {
      const response = await apiGet<CollectionCaseListItemApi[]>(
        "/api/v1/collection-cases/list",
        undefined,
        signal
      );
      return response.map(mapApiCaseToCollectionCase);
    },
    mockCases,
    { staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}

export function useCollectionCaseDetail(caseId: string | undefined) {
  const { apiGet } = useApi();

  const mockCase = caseId
    ? getGlobalDataStore().getAllCases().find((c) => c.caseId === caseId) || null
    : null;

  return useModeAwareQuery<CollectionCase | null, Error>(
    ["collection-case-detail", caseId],
    async ({ signal }) => {
      if (!caseId) return null;
      const response = await apiGet<CollectionCaseListItemApi>(
        `/api/v1/collection-cases/${caseId}`,
        undefined,
        signal
      );
      return mapApiCaseToCollectionCase(response);
    },
    mockCase,
    { enabled: !!caseId, staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}

function mockNotesForCase(caseId: string): CaseNote[] {
  return [
    {
      id: "1",
      caseId,
      content:
        "Customer called to discuss payment options. Mentioned they are waiting for a large client payment expected next week. Agreed to follow up on March 28th.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 21, 10, 30),
    },
    {
      id: "2",
      caseId,
      content:
        "Sent detailed payment plan proposal via email. Plan includes 3 monthly installments of £5,000 each. Awaiting customer response.",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 14, 15),
    },
    {
      id: "3",
      caseId,
      content:
        "Initial contact made. Customer acknowledged the outstanding balance and requested time to review their cash flow situation. Scheduled follow-up call for next week.",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 9, 45),
    },
    {
      id: "4",
      caseId,
      content:
        "Case assigned to collections team. Customer has been unresponsive to previous payment reminders. Priority set to High due to amount and age of debt.",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];
}

export function useCaseNotes(caseId: string | undefined) {
  const { apiGet } = useApi();

  const mockNotes = caseId ? mockNotesForCase(caseId) : [];

  return useModeAwareQuery<CaseNote[], Error>(
    ["case-notes", caseId],
    async ({ signal }) => {
      if (!caseId) return [];
      const response = await apiGet<CaseNoteApi[]>(
        `/api/v1/collection-cases/${caseId}/notes`,
        undefined,
        signal
      );
      return response.map((note) => ({
        id: String(note.id),
        caseId: note.caseId,
        content: note.content,
        createdBy: note.createdByUserName,
        createdAt: new Date(note.createdAt),
        updatedAt: note.updatedAt ? new Date(note.updatedAt) : undefined,
      }));
    },
    mockNotes,
    { enabled: !!caseId, staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}

function mockActivitiesForCase(caseId: string): CaseActivity[] {
  return [
    {
      id: "1",
      caseId,
      type: "call",
      description: "Attempted phone call - no answer",
      outcome: "Left voicemail requesting callback",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "2",
      caseId,
      type: "email",
      description: "Sent payment reminder email",
      outcome: "Email delivered successfully",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 9, 15),
    },
    {
      id: "3",
      caseId,
      type: "note",
      description: "Customer requested payment plan options",
      outcome: "Sent payment plan proposal",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 15, 11, 45),
    },
    {
      id: "4",
      caseId,
      type: "call",
      description: "Spoke with accounts payable manager",
      outcome: "Promised payment by end of week",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 12, 16, 20),
    },
  ];
}

export function useCaseActivities(caseId: string | undefined) {
  const { apiGet } = useApi();

  const mockActivities = caseId ? mockActivitiesForCase(caseId) : [];

  return useModeAwareQuery<CaseActivity[], Error>(
    ["case-activities", caseId],
    async ({ signal }) => {
      if (!caseId) return [];
      const response = await apiGet<CaseActivityApi[]>(
        `/api/v1/collection-cases/${caseId}/activities`,
        undefined,
        signal
      );
      return response.map((activity) => ({
        id: activity.activityId,
        caseId: activity.caseId,
        type: activity.activityType.toLowerCase() as CaseActivity["type"],
        description: activity.description,
        outcome: activity.outcome || undefined,
        createdBy: activity.createdBy,
        createdAt: new Date(activity.createdAt),
      }));
    },
    mockActivities,
    { enabled: !!caseId, staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}

function mockCommunicationsForCase(
  caseId: string,
  customerName: string
): CaseCommunication[] {
  return [
    {
      id: "1",
      caseId,
      type: "email",
      direction: "outbound",
      subject: "Payment Reminder - Account Overdue",
      content:
        "Dear Customer, This is a friendly reminder that your account has an outstanding balance...",
      sentAt: new Date(2024, 2, 18, 9, 15),
      sentBy: "Sarah Johnson",
      status: "delivered",
    },
    {
      id: "2",
      caseId,
      type: "email",
      direction: "inbound",
      subject: "RE: Payment Reminder",
      content:
        "Thank you for your email. We are experiencing temporary cash flow issues...",
      sentAt: new Date(2024, 2, 16, 14, 30),
      sentBy: customerName,
      status: "delivered",
    },
  ];
}

export function useCaseCommunications(
  caseId: string | undefined,
  customerName: string
) {
  const { apiGet } = useApi();

  const mockCommunications =
    caseId && customerName
      ? mockCommunicationsForCase(caseId, customerName)
      : [];

  return useModeAwareQuery<CaseCommunication[], Error>(
    ["case-communications", caseId],
    async ({ signal }) => {
      if (!caseId) return [];
      const response = await apiGet<CaseCommunicationApi[]>(
        `/api/v1/collection-cases/${caseId}/communications`,
        undefined,
        signal
      );
      return response.map((comm) => ({
        id: String(comm.id),
        caseId: comm.caseId,
        type: comm.communicationType.toLowerCase() as CaseCommunication["type"],
        direction: comm.direction.toLowerCase() as CaseCommunication["direction"],
        subject: comm.subject || undefined,
        content: comm.content,
        sentAt: comm.sentAt ? new Date(comm.sentAt) : new Date(comm.createdAt),
        sentBy: comm.sentByUserName || "System",
        status: comm.status.toLowerCase() as CaseCommunication["status"],
      }));
    },
    mockCommunications,
    { enabled: !!caseId, staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}

function mockAuditLogsForCase(
  caseId: string,
  caseAmount: number,
  customerEmail: string
): CaseAuditLog[] {
  return [
    {
      id: "audit-1",
      caseId,
      action: "status_change",
      description: "Case status changed",
      fieldName: "status",
      oldValue: "On Hold",
      newValue: "Active",
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 22, 9, 15),
    },
    {
      id: "audit-2",
      caseId,
      action: "priority_change",
      description: "Priority level updated",
      fieldName: "priority",
      oldValue: "Medium",
      newValue: "High",
      createdBy: "Mike Chen",
      createdAt: new Date(2024, 2, 20, 14, 30),
    },
    {
      id: "audit-3",
      caseId,
      action: "transaction_added",
      description: "Transaction added to case",
      transactionId: "3",
      transactionNumber: "INV-2024-003",
      transactionAmount: caseAmount * 0.25,
      transactionDate: new Date(2024, 2, 10),
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 19, 11, 20),
    },
    {
      id: "audit-4",
      caseId,
      action: "field_update",
      description: "Customer contact information updated",
      fieldName: "customerEmail",
      oldValue: "old.email@example.com",
      newValue: customerEmail,
      createdBy: "Sarah Johnson",
      createdAt: new Date(2024, 2, 18, 16, 45),
    },
    {
      id: "audit-5",
      caseId,
      action: "assignment_change",
      description: "Case reassigned to different collector",
      fieldName: "assignedTo",
      oldValue: "John Smith",
      newValue: "Sarah Johnson",
      createdBy: "System",
      createdAt: new Date(2024, 2, 15, 8, 30),
    },
    {
      id: "audit-10",
      caseId,
      action: "case_created",
      description: "Collection case created",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10, 8, 0),
    },
  ];
}

export function useCaseAuditLogs(
  caseId: string | undefined,
  caseAmount: number,
  customerEmail: string
) {
  const { apiGet } = useApi();

  const mockLogs =
    caseId ? mockAuditLogsForCase(caseId, caseAmount, customerEmail) : [];

  return useModeAwareQuery<CaseAuditLog[], Error>(
    ["case-audit-logs", caseId],
    async ({ signal }) => {
      if (!caseId) return [];
      const response = await apiGet<CaseAuditLogApi[]>(
        "/api/v1/audit-logs/cases",
        { caseId },
        signal
      );
      return response.map(mapApiAuditLogToCaseAuditLog);
    },
    mockLogs,
    { enabled: !!caseId, staleTime: 30 * 1000, refetchOnWindowFocus: false }
  );
}

function mockTransactionsForCase(
  customerId: string,
  customerName: string,
  caseAmount: number
): Transaction[] {
  return [
    {
      id: 1,
      referenceNumber: "INV-2024-001",
      transactionDate: new Date(2024, 0, 15).toISOString(),
      dueDate: new Date(2024, 1, 15).toISOString(),
      customerId: parseInt(customerId),
      customerName,
      transactionType: "invoice",
      amount: caseAmount * 0.4,
      outstandingAmount: caseAmount * 0.4,
      currency: "GBP",
      status: "overdue",
      description: "Invoice for services rendered - January 2024",
      createdBy: "System",
      createdAt: new Date(2024, 0, 15).toISOString(),
    },
    {
      id: 2,
      referenceNumber: "INV-2024-002",
      transactionDate: new Date(2024, 1, 1).toISOString(),
      dueDate: new Date(2024, 2, 1).toISOString(),
      customerId: parseInt(customerId),
      customerName,
      transactionType: "invoice",
      amount: caseAmount * 0.35,
      outstandingAmount: caseAmount * 0.35,
      currency: "GBP",
      status: "overdue",
      description: "Invoice for services rendered - February 2024",
      createdBy: "System",
      createdAt: new Date(2024, 1, 1).toISOString(),
    },
    {
      id: 3,
      referenceNumber: "INV-2024-003",
      transactionDate: new Date(2024, 2, 10).toISOString(),
      dueDate: new Date(2024, 3, 10).toISOString(),
      customerId: parseInt(customerId),
      customerName,
      transactionType: "invoice",
      amount: caseAmount * 0.25,
      outstandingAmount: caseAmount * 0.25,
      currency: "GBP",
      status: "outstanding",
      description: "Invoice for services rendered - March 2024",
      createdBy: "System",
      createdAt: new Date(2024, 2, 10).toISOString(),
    },
  ];
}

export function useCaseTransactions(
  customerId: string | undefined,
  customerName: string,
  caseAmount: number
) {
  const { apiGet } = useApi();

  const mockTransactions =
    customerId
      ? mockTransactionsForCase(customerId, customerName, caseAmount)
      : [];

  return useModeAwareQuery<Transaction[], Error>(
    ["case-transactions", customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      const response = await apiGet<{
        data: {
          id: number;
          referenceNumber: string;
          transactionDate: string;
          dueDate: string | null;
          customerId: number;
          customerName: string;
          transactionType: string;
          amount: number;
          outstandingAmount: number;
          currency: string;
          status: string;
          description: string | null;
          createdBy: string | null;
          createdAt: string;
        }[];
      }>(
        "/api/v1/transactions",
        { customerId, pageSize: 50, transactionType: "invoice" },
        signal
      );
      return response.data.map((t) => ({
        id: Number(t.id),
        referenceNumber: t.referenceNumber,
        transactionDate: t.transactionDate,
        dueDate: t.dueDate || "",
        customerId: t.customerId,
        customerName: t.customerName,
        transactionType: t.transactionType as Transaction["transactionType"],
        amount: t.amount,
        outstandingAmount: t.outstandingAmount,
        currency: t.currency || "GBP",
        status: t.status as Transaction["status"],
        description: t.description || "",
        createdBy: t.createdBy || "System",
        createdAt: t.createdAt,
      }));
    },
    mockTransactions,
    {
      enabled: !!customerId,
      staleTime: 30 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}

function mockContactsForCustomer(customerId: string): ContactUI[] {
  const contactSets: Record<string, ContactUI[]> = {
    "1": [
      {
        id: "contact-1-1",
        firstName: "John",
        surname: "Smith",
        email: "john.smith@acmecorp.com",
        phone: "+44 20 7946 0301",
        jobTitle: "Accounts Payable Manager",
        company: "Acme Corporation",
        customerId: "1",
        address: "123 Business St, New York, NY 10001",
        notes: "Primary contact for all payment-related matters",
        createdAt: new Date(2023, 0, 15).toISOString(),
        lastContact: new Date(2024, 2, 20).toISOString(),
        isPrimary: true,
      },
      {
        id: "contact-1-2",
        firstName: "Sarah",
        surname: "Johnson",
        email: "sarah.johnson@acmecorp.com",
        phone: "+44 20 7946 0302",
        jobTitle: "Finance Director",
        company: "Acme Corporation",
        customerId: "1",
        address: "123 Business St, New York, NY 10001",
        notes: "Escalation contact for urgent matters",
        createdAt: new Date(2023, 1, 10).toISOString(),
        lastContact: new Date(2024, 2, 15).toISOString(),
        isPrimary: false,
      },
      {
        id: "contact-1-3",
        firstName: "Michael",
        surname: "Chen",
        email: "michael.chen@acmecorp.com",
        phone: "+44 20 7946 0303",
        jobTitle: "Accounts Payable Clerk",
        company: "Acme Corporation",
        customerId: "1",
        address: "123 Business St, New York, NY 10001",
        notes: "Day-to-day invoice queries",
        createdAt: new Date(2023, 3, 5).toISOString(),
        lastContact: new Date(2024, 2, 18).toISOString(),
        isPrimary: false,
      },
    ],
    "2": [
      {
        id: "contact-2-1",
        firstName: "Emily",
        surname: "Davis",
        email: "emily.davis@techstart.com",
        phone: "+44 121 496 0304",
        jobTitle: "CFO",
        company: "TechStart Inc",
        customerId: "2",
        address: "456 Tech Ave, San Francisco, CA 94102",
        notes: "Primary financial decision maker",
        createdAt: new Date(2023, 2, 20).toISOString(),
        lastContact: new Date(2024, 2, 22).toISOString(),
        isPrimary: true,
      },
      {
        id: "contact-2-2",
        firstName: "Robert",
        surname: "Wilson",
        email: "robert.wilson@techstart.com",
        phone: "+44 121 496 0305",
        jobTitle: "Accounting Manager",
        company: "TechStart Inc",
        customerId: "2",
        address: "456 Tech Ave, San Francisco, CA 94102",
        notes: "Handles invoice processing",
        createdAt: new Date(2023, 4, 12).toISOString(),
        lastContact: new Date(2024, 2, 19).toISOString(),
        isPrimary: false,
      },
    ],
  };

  return (
    contactSets[customerId] || [
      {
        id: `contact-${customerId}-1`,
        firstName: "Default",
        surname: "Contact",
        email: `contact@customer${customerId}.com`,
        phone: "+44 800 123 4567",
        jobTitle: "Contact Person",
        company: `Customer ${customerId}`,
        customerId,
        address: "Address not available",
        notes: "Default contact",
        createdAt: new Date(2023, 0, 1).toISOString(),
        lastContact: new Date(2024, 0, 1).toISOString(),
        isPrimary: true,
      },
    ]
  );
}

export function useCaseContacts(customerId: string | undefined) {
  const { apiGet } = useApi();

  const mockContacts = customerId ? mockContactsForCustomer(customerId) : [];

  return useModeAwareQuery<ContactUI[], Error>(
    ["case-contacts", customerId],
    async ({ signal }) => {
      if (!customerId) return [];
      try {
        const response = await apiGet<ContactsApiResponse>(
          `/api/v1/customers/${customerId}/contacts`,
          undefined,
          signal
        );
        if (
          response &&
          typeof response === "object" &&
          "contacts" in response &&
          Array.isArray(response.contacts)
        ) {
          return response.contacts.map((c) => ({
            id: String(c.id),
            firstName: c.firstName,
            surname: c.surname,
            email: c.email,
            phone: c.phone,
            jobTitle: c.jobTitle || "",
            company: c.company || "",
            customerId: String(customerId),
            address: c.address || "",
            notes: c.notes || "",
            createdAt: c.createdAt || "",
            lastContact: c.lastContact || "",
            isPrimary: c.isPrimary,
          }));
        }
        return mockContactsForCustomer(customerId);
      } catch {
        return mockContactsForCustomer(customerId);
      }
    },
    mockContacts,
    {
      enabled: !!customerId,
      staleTime: 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );
}
